import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type RefObject } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { api } from '@shared/api';
import { BRAND_NAME } from '@shared/brand';
import { Icon } from '../components/ui';
import { DEFAULT_IMAGE_MODEL, DEFAULT_MODEL, DEFAULT_VIDEO_MODEL, LANGS, type Lang } from '../snippets';
import { DocsContext, pick, type CatalogModel, type DocsState } from './docs/context';
import { GROUPS, LEGACY_HASH, PAGES, docPath } from './docs/registry';
import { Overview } from './docs/Overview';
import { AuthPage, ModelsPage } from './docs/StartPages';
import { ChatPage, ToolsPage } from './docs/TextPages';
import { ImagesPage, VideoPage } from './docs/MediaPages';
import { BillingPage, ErrorsPage, FaqPage, IntegrationsPage } from './docs/ReferencePages';
import { ClaudeCodePage, ClinePage, CodexPage } from './docs/AgentPages';
import { AnthropicPage, CompletionsPage, EmbeddingsPage, ResponsesPage } from './docs/ApiPages';
import { SearchTrigger } from '../search/SearchTrigger';
import '../styles/docs.css';

const VIEWS: Record<string, ComponentType> = {
  '': Overview,
  autentikasi: AuthPage,
  model: ModelsPage,
  chat: ChatPage,
  tools: ToolsPage,
  anthropic: AnthropicPage,
  responses: ResponsesPage,
  embeddings: EmbeddingsPage,
  completions: CompletionsPage,
  gambar: ImagesPage,
  video: VideoPage,
  biaya: BillingPage,
  error: ErrorsPage,
  integrasi: IntegrationsPage,
  'claude-code': ClaudeCodePage,
  codex: CodexPage,
  cline: ClinePage,
  faq: FaqPage,
};

const LANG_KEY = 'aiagg.docs.lang';

function readLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    return LANGS.some((l) => l.id === stored) ? (stored as Lang) : 'python';
  } catch {
    return 'python';
  }
}

function useCatalog(enabled: boolean) {
  const [models, setModels] = useState<CatalogModel[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    api<{ data: CatalogModel[] }>('/v1/models')
      .then((body) => alive && setModels(body.data))
      .catch((err: Error) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, [enabled]);
  return { models, error };
}

interface TocItem {
  id: string;
  label: string;
}

const SPY_LINE = 140;

/** Headings + active heading. */
function useOnPage(slug: string, root: RefObject<HTMLElement>) {
  const [items, setItems] = useState<TocItem[]>([]);
  const [active, setActive] = useState('');

  useEffect(() => {
    const heads = root.current?.querySelectorAll<HTMLElement>('section.docs-part[id] > h2') ?? [];
    setItems(Array.from(heads).map((h) => ({ id: (h.parentElement as HTMLElement).id, label: h.textContent ?? '' })));
  }, [slug, root]);

  useEffect(() => {
    if (items.length < 2) return;
    const onScroll = () => {
      let hit = items[0].id;
      for (const item of items) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= SPY_LINE) hit = item.id;
      }
      // short last sections
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) hit = items[items.length - 1].id;
      setActive(hit);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [items]);

  return { items, active };
}

/** Mobile topic menu. */
function MobileNav({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const page = PAGES.find((p) => p.slug === slug) ?? PAGES[0];

  useEffect(() => setOpen(false), [slug]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="docs-mnav" ref={ref}>
      <div className="docs-mnav-bar">
        <button type="button" className="docs-mnav-btn" aria-expanded={open} aria-controls="docs-mnav-panel" onClick={() => setOpen((v) => !v)}>
          <Icon name={page.icon} size={16} />
          <span className="docs-mnav-current">{page.nav}</span>
          <Icon name="chevron" size={16} className="docs-mnav-chevron" />
        </button>
        <SearchTrigger variant="icon" placeholder="Cari dokumentasi" />
      </div>
      {open && (
        <div className="docs-mnav-panel" id="docs-mnav-panel">
          {GROUPS.map((group) => (
            <div key={group} className="docs-mnav-group">
              <div className="docs-nav-label">{group}</div>
              {PAGES.filter((p) => p.group === group).map((p) => (
                <Link key={p.slug} to={docPath(p.slug)} aria-current={p.slug === slug ? 'page' : undefined}>
                  <Icon name={p.icon} size={16} />
                  {p.nav}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Docs({ publicView = false }: { publicView?: boolean }) {
  const params = useParams();
  const slug = params.slug ?? '';
  const { hash } = useLocation();
  const articleRef = useRef<HTMLElement>(null);
  const [lang, setLangState] = useState<Lang>(readLang);
  const { models, error: catalogError } = useCatalog(!publicView);
  const { items: onPage, active } = useOnPage(slug, articleRef);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch {
      // storage blocked
    }
  }, []);

  const state = useMemo<DocsState>(() => {
    const text = pick(models, 'text', DEFAULT_MODEL);
    const image = pick(models, 'image', DEFAULT_IMAGE_MODEL);
    const video = pick(models, 'video', DEFAULT_VIDEO_MODEL);
    return {
      publicView,
      lang,
      setLang,
      models,
      catalogError,
      text,
      image,
      video,
      model: text?.id ?? DEFAULT_MODEL,
      imageModel: image?.id ?? DEFAULT_IMAGE_MODEL,
      videoModel: video?.id ?? DEFAULT_VIDEO_MODEL,
    };
  }, [publicView, lang, setLang, models, catalogError]);

  const legacy = slug === '' ? LEGACY_HASH[decodeURIComponent(hash.slice(1))] : undefined;
  const page = PAGES.find((p) => p.slug === slug);

  // scroll on navigation
  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    if (!id) {
      window.scrollTo({ top: 0 });
      return;
    }
    const frame = requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(frame);
  }, [slug, hash]);

  useEffect(() => {
    if (!page) return;
    const before = document.title;
    document.title = slug ? `${page.title} · Dokumentasi ${BRAND_NAME}` : `Dokumentasi ${BRAND_NAME}`;
    return () => {
      document.title = before;
    };
  }, [page, slug]);

  if (legacy) return <Navigate to={legacy} replace />;
  if (!page || !(slug in VIEWS)) return <Navigate to="/docs" replace />;

  const View = VIEWS[slug];
  const index = PAGES.indexOf(page);
  const prev = PAGES[index - 1];
  const next = PAGES[index + 1];

  return (
    <DocsContext.Provider value={state}>
      <div className={publicView ? 'docs docs-public' : 'docs'}>
        <div className="docs-shell">
          <nav className="docs-nav" aria-label="Dokumentasi">
            <SearchTrigger variant="sidebar" placeholder="Cari dokumentasi" />
            {GROUPS.map((group, i) => (
              <div key={group} className="docs-nav-group">
                <div className="docs-nav-label" id={`docs-nav-${i}`}>
                  {group}
                </div>
                <ul aria-labelledby={`docs-nav-${i}`}>
                  {PAGES.filter((p) => p.group === group).map((p) => (
                    <li key={p.slug}>
                      <Link to={docPath(p.slug)} aria-current={p.slug === slug ? 'page' : undefined}>
                        <Icon name={p.icon} size={16} />
                        {p.nav}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <MobileNav slug={slug} />

          <article className="docs-article" ref={articleRef}>
            <View />

            <nav className="docs-pager" aria-label="Halaman sebelum dan sesudah">
              {prev && (
                <Link className="docs-pager-link prev" to={docPath(prev.slug)}>
                  <span className="docs-pager-label">
                    <Icon name="left" size={15} />
                    Sebelumnya
                  </span>
                  <span className="docs-pager-title">{prev.nav}</span>
                </Link>
              )}
              {next && (
                <Link className="docs-pager-link next" to={docPath(next.slug)}>
                  <span className="docs-pager-label">
                    Berikutnya
                    <Icon name="right" size={15} />
                  </span>
                  <span className="docs-pager-title">{next.nav}</span>
                </Link>
              )}
            </nav>

            <div className="docs-help">
              <Icon name="help" size={16} />
              <span>
                Belum menemukan jawabannya? Lihat <Link to={docPath('faq')}>Pertanyaan umum</Link> atau tulis ke{' '}
                <a href="mailto:halo@partnerai.id">halo@partnerai.id</a>.
              </span>
            </div>
          </article>

          {onPage.length > 1 && (
            <aside className="docs-onpage" aria-labelledby="docs-onpage-title">
              <div className="docs-onpage-title" id="docs-onpage-title">
                Di halaman ini
              </div>
              <ul>
                {onPage.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} className={item.id === active ? 'active' : undefined}>
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </div>
    </DocsContext.Provider>
  );
}
